import { validateEmail } from "../../utils/validationUtils";
import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { SPOC_EMPTY_FORM } from "./SpocFields";
import { createSpocAction } from "../../actions/createSpocAction";
import { updateSpocAction } from "../../actions/updateSpocAction";
import { fetchSpocListAction } from "../../actions/fetchSpocListAction";
import { toggleSpocStatusAction } from "../../actions/toggleSpocStatusAction";

const PAGE_SIZE = 10;

const validateSpocForm = (form, isEdit = false) => {
  const errors = {};

  if (isEdit) {
    if (!form.lob?.trim()) {
      errors.lob = "Entity LOB Type is required";
    }
    if (!form.department?.trim()) {
      errors.department = "AA - Department Function is required";
    }
    return errors;
  }

  // ── Create validation ─────────────────────────────────────────────────────
  if (!form.employeeName?.trim()) {
    errors.employeeName = "Employee Name is required";
  } else if (form.employeeName.trim().length < 3) {
    errors.employeeName = "Employee Name must be at least 3 characters";
  }

  if (!form.email?.trim()) {
    errors.email = "Email ID is required";
  } else {
    const emailResult = validateEmail(form.email);
    if (emailResult !== true) {
      errors.email = emailResult;
    }
  }

  if (!form.password?.trim()) {
    errors.password = "Password is required";
  } else if (form.password.trim().length < 8) {
    errors.password = "Password must be at least 8 characters";
  }

  if (!form.lob?.trim()) {
    errors.lob = "Entity LOB Type is required";
  }

  if (!form.department?.trim()) {
    errors.department = "AA - Department Function is required";
  }

  if (!form.role?.trim()) {
    errors.role = "Role is required";
  }

  return errors;
};

export const useSpocManagement = () => {
  const [spocList, setSpocList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedSpoc, setSelectedSpoc] = useState(null);
  const [formData, setFormData] = useState({ ...SPOC_EMPTY_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusValue, setStatusValue] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);

  const debounceRef = useRef(null);

  const loadList = useCallback(async (page = 1, search = "") => {
    setLoading(true);
    try {
      const result = await fetchSpocListAction({
        searchValue: search?.trim() || undefined,
        pageNumber: page,
        pageSize: PAGE_SIZE,
      });

      if (result.success) {
        setSpocList(result.data);
        setTotalRecords(result.totalRecords);
      } else {
        toast.error(result.message || "Failed to load SPOC list");
        setSpocList([]);
        setTotalRecords(0);
      }
    } catch {
      toast.error("Failed to load SPOC list");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList(currentPage, searchQuery);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      loadList(1, value);
    }, 400);
  };

  const openAddModal = () => {
    setFormData({ ...SPOC_EMPTY_FORM });
    setFormErrors({});
    setSelectedSpoc(null);
    setModalMode("add");
    setModalOpen(true);
  };

  const openViewModal = (spoc) => {
    setSelectedSpoc(spoc);
    setFormData({ ...spoc });
    setFormErrors({});
    setModalMode("view");
    setModalOpen(true);
  };

  const openEditModal = (spoc) => {
    setSelectedSpoc(spoc);
    setFormData({ ...spoc, password: "" });
    setFormErrors({});
    setModalMode("edit");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setFormData({ ...SPOC_EMPTY_FORM });
    setFormErrors({});
    setSelectedSpoc(null);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "lob") {
        next.department = "";
      }
      return next;
    });

    setFormErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      if (name === "lob") delete next.department;
      return next;
    });
  };

  const handleSubmit = async () => {
    const isEdit = modalMode === "edit";
    const errors = validateSpocForm(formData, isEdit);
    if (Object.keys(errors).length > 0) {
      console.warn(
        "[SpocManagement] Validation failed:",
        errors,
        "| formData:",
        formData,
      );
      setFormErrors(errors);
      return;
    }

    // console.log(
    //   "[SpocManagement] handleSubmit | mode:",
    //   modalMode,
    //   "| formData:",
    //   formData,
    // );
    setSaving(true);
    try {
      if (isEdit) {
        const result = await updateSpocAction(selectedSpoc.id, formData);
        if (!result.success) {
          toast.error(result.message);
          return;
        }
        toast.success(result.message || "SPOC updated successfully!");
      } else {
        const result = await createSpocAction(formData);
        if (!result.success) {
          toast.error(result.message);
          return;
        }
        toast.success(result.message || "SPOC created successfully!");
      }
      closeModal();
      await loadList(currentPage, searchQuery);
    } catch {
      toast.error("An error occurred. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const openStatusModal = (spoc) => {
    setStatusTarget(spoc);
    setStatusValue(spoc.isActive !== false);
    setStatusModalOpen(true);
  };

  const closeStatusModal = () => {
    setStatusModalOpen(false);
    setStatusTarget(null);
  };

  const handleStatusSubmit = async () => {
    if (!statusTarget) return;

    setStatusLoading(true);
    console.log(
      `[SpocManagement] toggleStatus | id=${statusTarget.id} | isActive=${statusValue}`,
    );
    try {
      const result = await toggleSpocStatusAction(statusTarget.id, statusValue);
      if (!result.success) {
        toast.error(result.message);
        return;
      }
      toast.success(
        result.message ||
          `SPOC ${statusValue ? "activated" : "deactivated"} successfully!`,
      );
      closeStatusModal();
      await loadList(currentPage, searchQuery);
    } catch {
      toast.error("Failed to update SPOC status");
    } finally {
      setStatusLoading(false);
    }
  };

  return {
    spocList,
    totalRecords,
    currentPage,
    setCurrentPage,
    loading,
    searchQuery,
    handleSearchChange,
    loadList,
    modalOpen,
    modalMode,
    formData,
    formErrors,
    saving,
    selectedSpoc,
    openAddModal,
    openViewModal,
    openEditModal,
    closeModal,
    handleFieldChange,
    handleSubmit,
    statusModalOpen,
    statusTarget,
    statusValue,
    setStatusValue,
    statusLoading,
    openStatusModal,
    closeStatusModal,
    handleStatusSubmit,
  };
};
